import * as React from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DndContextProps,
  DragOverlay,
  UniqueIdentifier,
  CollisionDetection,
} from '@dnd-kit/core';
import type { KeyboardCoordinateGetter } from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import type { SortableItemRenderProps } from '../../types/controls';

export interface UseSortableArrayProps {
  /**
   * Array of unique string IDs for the items
   */
  items: string[];
  /**
   * Callback when items are reordered (fromIndex, toIndex)
   */
  onReorder: (fromIndex: number, toIndex: number) => void;
  /**
   * Disable sorting
   */
  disabled?: boolean;
  /**
   * Optional label for the items (e.g., "Question", "Step", "Item")
   * Used in screen reader announcements
   */
  itemLabel?: string;
}

export interface SortableItemProps {
  /**
   * The unique ID for this sortable item
   */
  id: string;
  /**
   * Whether this item is disabled
   */
  disabled?: boolean;
}

export interface UseSortableArrayReturn {
  /**
   * Props to spread on the DndContext component
   */
  dndContextProps: Pick<
    DndContextProps,
    | 'sensors'
    | 'collisionDetection'
    | 'onDragStart'
    | 'onDragEnd'
    | 'modifiers'
    | 'accessibility'
  >;
  /**
   * Props for the SortableContext
   */
  sortableContextProps: {
    items: string[];
    strategy: typeof verticalListSortingStrategy;
  };
  /**
   * Props for the container wrapper
   */
  containerWrapperProps: {
    ref: React.RefObject<HTMLDivElement>;
    style: React.CSSProperties;
  };
  /**
   * Hook to get sortable item props for a specific item
   */
  useSortableItem: (props: SortableItemProps) => SortableItemRenderProps;
  /**
   * Context value for focus management
   */
  FocusContext: React.Context<FocusContextValue | null>;
  /**
   * Value to provide to the focus context
   */
  focusContextValue: FocusContextValue;
  /**
   * Context value for settling state
   */
  SettlingContext: React.Context<boolean>;
  /**
   * Value to provide to the settling context
   */
  settlingContextValue: boolean;
  /**
   * ID of the currently active drag item (null if not dragging)
   */
  activeDragId: string | null;
  /**
   * DragOverlay component from @dnd-kit/core
   */
  DragOverlay: typeof DragOverlay;
}

interface FocusContextValue {
  registerHandle: (id: string, element: HTMLElement | null) => void;
  focusHandle: (id: string) => void;
}

/**
 * Custom collision detection that uses directional midpoint logic:
 * - When moving UP: collision detected when top edge of dragged item passes midpoint of target
 * - When moving DOWN: collision detected when bottom edge of dragged item passes midpoint of target
 */
const createDirectionalMidpointCollision =
  (previousTopRef: React.MutableRefObject<number | null>): CollisionDetection =>
  ({ active, droppableContainers, collisionRect }) => {
    if (!collisionRect) {
      return [];
    }

    // Determine direction based on previous position
    const previousTop = previousTopRef.current;
    const isMovingDown =
      previousTop !== null && collisionRect.top > previousTop;
    const isMovingUp = previousTop !== null && collisionRect.top < previousTop;

    // Update the previous position for next call
    // eslint-disable-next-line no-param-reassign
    previousTopRef.current = collisionRect.top;

    const collisions: Array<{
      id: UniqueIdentifier;
      value: number;
    }> = [];

    // Check each droppable container for collision
    Array.from(droppableContainers.values()).forEach((droppableContainer) => {
      const { id } = droppableContainer;
      const { rect } = droppableContainer;

      // Skip the active item itself or items without rects
      if (id === active.id || !rect.current) {
        return;
      }

      const rectCurrent = rect.current;
      const targetMidpoint = rectCurrent.top + rectCurrent.height / 2;

      let hasCollision = false;

      if (isMovingDown) {
        // When moving down, check if bottom edge of dragged item passed target midpoint
        hasCollision = collisionRect.bottom >= targetMidpoint;
      } else if (isMovingUp) {
        // When moving up, check if top edge of dragged item passed target midpoint
        hasCollision = collisionRect.top <= targetMidpoint;
      } else {
        // No previous position, use simple overlap check
        hasCollision =
          collisionRect.bottom > rectCurrent.top &&
          collisionRect.top < rectCurrent.bottom;
      }

      if (hasCollision) {
        // Calculate distance from dragged item center to target center for sorting
        const draggedCenter = collisionRect.top + collisionRect.height / 2;
        const targetCenter = rectCurrent.top + rectCurrent.height / 2;
        const distance = Math.abs(draggedCenter - targetCenter);

        collisions.push({
          id,
          value: distance,
        });
      }
    });

    // Sort by distance, closest first
    return collisions.sort((a, b) => a.value - b.value);
  };

/**
 * Hook that provides all the drag-and-drop functionality for sortable arrays.
 * This hook manages the DnD context, sensors, focus management, and item settling.
 */
export const useSortableArray = ({
  items,
  onReorder,
  disabled = false,
  itemLabel = 'Item',
}: UseSortableArrayProps): UseSortableArrayReturn => {
  const handleRefsRef = React.useRef<Map<string, HTMLElement>>(new Map());
  const handleCleanupRef = React.useRef<Map<string, () => void>>(new Map());
  const lastFocusedHandleIdRef = React.useRef<string | null>(null);
  const nodeRefsRef = React.useRef<Map<string, HTMLElement>>(new Map());
  const hadFocusRef = React.useRef(false);
  const pendingFocusIdRef = React.useRef<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isSettling, setIsSettling] = React.useState(false);
  const [settlingHeight, setSettlingHeight] = React.useState<
    number | undefined
  >(undefined);
  const [activeDragId, setActiveDragId] = React.useState<string | null>(null);
  const draggedDimensionsRef = React.useRef<{
    width: number;
    height: number;
  } | null>(null);
  const previousActiveTopRef = React.useRef<number | null>(null);
  const visualIndexRef = React.useRef<number | null>(null);

  // Create contexts
  const FocusContext = React.useMemo(
    () => React.createContext<FocusContextValue | null>(null),
    [],
  );
  const SettlingContext = React.useMemo(
    () => React.createContext<boolean>(false),
    [],
  );

  // Custom coordinate getter for keyboard that moves by the target element's height
  // Tracks visual position during drag
  const customKeyboardCoordinates: KeyboardCoordinateGetter = React.useCallback(
    (event, args) => {
      const { active, currentCoordinates } = args;
      const activeId = String(active);

      // Use visual index if available, otherwise fall back to array position
      const currentIndex = visualIndexRef.current ?? items.indexOf(activeId);

      if (currentIndex === -1) {
        return sortableKeyboardCoordinates(event, args);
      }

      let targetIndex: number;

      if (event.code === 'ArrowDown') {
        targetIndex = currentIndex + 1;
      } else if (event.code === 'ArrowUp') {
        targetIndex = currentIndex - 1;
      } else {
        // For other keys, use default behavior
        return sortableKeyboardCoordinates(event, args);
      }

      // Clamp to valid bounds (no out of bounds errors)
      const clampedIndex = Math.max(0, Math.min(items.length - 1, targetIndex));

      // Update visual index
      visualIndexRef.current = clampedIndex;

      // Get the target element at the clamped index
      const targetId = items[clampedIndex];
      const targetElement = targetId ? nodeRefsRef.current.get(targetId) : null;

      if (!targetElement) {
        // No target element found - just use default keyboard movement
        return sortableKeyboardCoordinates(event, args);
      }

      // Move by the target element's height
      const targetHeight = targetElement.getBoundingClientRect().height;

      if (event.code === 'ArrowDown') {
        return {
          x: currentCoordinates.x,
          y: currentCoordinates.y + targetHeight,
        };
      }

      return {
        x: currentCoordinates.x,
        y: currentCoordinates.y - targetHeight,
      };
    },
    [items],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts (helps with clicks)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: customKeyboardCoordinates,
    }),
  );

  const registerHandle = React.useCallback(
    (id: string, element: HTMLElement | null) => {
      const cleanup = handleCleanupRef.current.get(id);
      if (cleanup) {
        cleanup();
        handleCleanupRef.current.delete(id);
      }

      if (element) {
        const handleFocus = () => {
          lastFocusedHandleIdRef.current = id;
        };
        const handleBlur = () => {
          if (lastFocusedHandleIdRef.current === id) {
            lastFocusedHandleIdRef.current = null;
          }
        };

        element.addEventListener('focus', handleFocus);
        element.addEventListener('blur', handleBlur);

        handleCleanupRef.current.set(id, () => {
          element.removeEventListener('focus', handleFocus);
          element.removeEventListener('blur', handleBlur);
        });

        handleRefsRef.current.set(id, element);
        if (document.activeElement === element) {
          lastFocusedHandleIdRef.current = id;
        }
        if (pendingFocusIdRef.current === id) {
          requestAnimationFrame(() => {
            element.focus({ preventScroll: true });
          });
          pendingFocusIdRef.current = null;
        }
      } else {
        handleRefsRef.current.delete(id);
        const existingCleanup = handleCleanupRef.current.get(id);
        if (existingCleanup) {
          existingCleanup();
          handleCleanupRef.current.delete(id);
        }
      }
    },
    [],
  );

  const focusHandle = React.useCallback((id: string) => {
    const element = handleRefsRef.current.get(id);
    if (element) {
      setTimeout(() => {
        element.focus({ preventScroll: true });
      }, 0);
      pendingFocusIdRef.current = null;
    } else {
      pendingFocusIdRef.current = id;
      setTimeout(() => {
        const fallback = document.querySelector(
          `[data-drag-handle-id="${id}"]`,
        ) as HTMLElement | null;
        if (fallback) {
          fallback.focus({ preventScroll: true });
          pendingFocusIdRef.current = null;
        }
      }, 250);
    }
  }, []);

  const focusContextValue = React.useMemo(
    () => ({ registerHandle, focusHandle }),
    [registerHandle, focusHandle],
  );

  const handleDragStart = React.useCallback(
    (event: DragStartEvent) => {
      const activeElement = document.activeElement as HTMLElement | null;
      const activeId = event.active.id as string;
      const dragHandle = handleRefsRef.current.get(activeId);
      const { activatorEvent } = event as DragStartEvent & {
        activatorEvent?: Event;
      };
      const isKeyboardActivator =
        typeof KeyboardEvent !== 'undefined' &&
        activatorEvent instanceof KeyboardEvent;

      const hadFocus =
        (activeElement && dragHandle && activeElement === dragHandle) ||
        lastFocusedHandleIdRef.current === activeId ||
        isKeyboardActivator;

      hadFocusRef.current = Boolean(hadFocus);

      // Capture dimensions of the dragged item and reset position tracking
      setActiveDragId(activeId);
      const draggedElement = nodeRefsRef.current.get(activeId);
      if (draggedElement) {
        draggedDimensionsRef.current = {
          width: draggedElement.offsetWidth,
          height: draggedElement.offsetHeight,
        };
      }
      // Reset previous top for new drag (collision detection will set it on first call)
      previousActiveTopRef.current = null;
      // Initialize visual index to the starting array position
      visualIndexRef.current = items.indexOf(activeId);
    },
    [items],
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      const shouldRefocus = hadFocusRef.current;
      const activeId = active.id as string;
      const overId = over?.id as string | undefined;
      const oldIndex = items.indexOf(activeId);
      const overIndex = overId ? items.indexOf(overId) : -1;
      let focusTargetId = activeId;

      if (over && oldIndex !== -1 && overIndex !== -1) {
        // After reordering, sortable IDs are regenerated based on new indices
        // Extract base ID and construct the new ID for the destination position
        const baseId = activeId.substring(0, activeId.lastIndexOf('-'));
        focusTargetId = `${baseId}-${overIndex}`;
      }

      const alternateFocusId =
        over && overIndex !== -1 ? (over.id as string) : null;

      if (
        over &&
        active.id !== over.id &&
        oldIndex !== -1 &&
        overIndex !== -1
      ) {
        // Capture scroll position before reordering
        const { scrollX, scrollY } = window;

        // Capture current container height before repositioning
        if (containerRef.current) {
          setSettlingHeight(containerRef.current.offsetHeight);
        }

        // Show overlay to mask repositioning
        setIsSettling(true);

        onReorder(oldIndex, overIndex);

        // Remove overlay after items have settled
        setTimeout(() => {
          setIsSettling(false);
          setSettlingHeight(undefined);
        }, 150);

        // Focus management for keyboard accessibility
        if (shouldRefocus) {
          setTimeout(() => {
            requestAnimationFrame(() => {
              focusHandle(focusTargetId);
              // Fallback to alternate focus if primary focus fails
              if (alternateFocusId && alternateFocusId !== focusTargetId) {
                const fallbackId = alternateFocusId;
                setTimeout(() => {
                  const activeElement =
                    document.activeElement as HTMLElement | null;
                  const activeHandleId = activeElement?.getAttribute(
                    'data-drag-handle-id',
                  );
                  if (activeHandleId !== focusTargetId) {
                    focusHandle(fallbackId);
                  }
                }, 150);
              }
              // Restore scroll position after focus is set
              setTimeout(() => {
                window.scrollTo(scrollX, scrollY);
              }, 200);
            });
          }, 200);
        } else {
          // Restore scroll even when not refocusing
          setTimeout(() => {
            window.scrollTo(scrollX, scrollY);
          }, 400);
        }
      }

      if (shouldRefocus && (!over || active.id === over.id)) {
        requestAnimationFrame(() => {
          focusHandle(focusTargetId);
        });
      }

      hadFocusRef.current = false;
      setActiveDragId(null);
      draggedDimensionsRef.current = null;
      previousActiveTopRef.current = null;
      visualIndexRef.current = null;
    },
    [items, onReorder, focusHandle],
  );

  // Create collision detection that tracks the previous position for directional awareness
  const collisionDetection = React.useMemo(
    () => createDirectionalMidpointCollision(previousActiveTopRef),
    [], // Create once and reuse (it uses the ref which updates)
  );

  const dndContextProps = React.useMemo(
    () => ({
      sensors,
      collisionDetection,
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
      modifiers: [restrictToVerticalAxis],
      accessibility: {
        screenReaderInstructions: {
          draggable: `Press space or enter to pick up the ${itemLabel}. While dragging, use arrow keys to move the item up or down. Press space or enter again to drop the item in its new position. Press escape to cancel.`,
        },
        announcements: {
          onDragStart({ active }: any) {
            const index = items.indexOf(active.id as string);
            return `${itemLabel} ${index + 1} picked up.`;
          },
          onDragOver({ active, over }: any) {
            if (!over) return '';
            const activeIndex = items.indexOf(active.id as string);
            const overIndex = items.indexOf(over.id as string);
            if (activeIndex === overIndex) return '';
            return `Moved to position ${overIndex + 1}.`;
          },
          onDragEnd({ over }: any) {
            if (!over) return '';
            const overIndex = items.indexOf(over.id as string);
            return `Dropped at position ${overIndex + 1}.`;
          },
          onDragCancel() {
            return 'Cancelled.';
          },
          onDragMove() {
            return ''; // Suppress verbose default message
          },
        },
      },
    }),
    [
      sensors,
      collisionDetection,
      handleDragStart,
      handleDragEnd,
      items,
      itemLabel,
    ],
  );

  const sortableContextProps = React.useMemo(
    () => ({
      items,
      strategy: verticalListSortingStrategy,
    }),
    [items],
  );

  const containerWrapperProps = React.useMemo(
    () => ({
      ref: containerRef,
      style: {
        width: '100%',
        minHeight: settlingHeight ? `${settlingHeight}px` : undefined,
      } as React.CSSProperties,
    }),
    [settlingHeight],
  );

  // Hook to be called within the context to get props for individual items
  const useSortableItem = ({
    id,
    disabled: itemDisabled = false,
  }: SortableItemProps): SortableItemRenderProps => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id,
      disabled: itemDisabled || disabled,
    });

    const handleRef = React.useCallback(
      (element: HTMLElement | null) => {
        registerHandle(id, element);
      },
      [id],
    );

    // Wrap setNodeRef to also store it in our node refs map
    const wrappedSetNodeRef = React.useCallback(
      (node: HTMLElement | null) => {
        setNodeRef(node);
        if (node) {
          nodeRefsRef.current.set(id, node);
        } else {
          nodeRefsRef.current.delete(id);
        }
      },
      [id, setNodeRef],
    );

    // Preserve original dimensions during drag to prevent squishing
    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      // Disable transition on drop to prevent jitter when items snap to final positions
      transition: transform ? transition : 'none',
    };

    // When dragging, explicitly lock width and height to maintain dimensions
    if (isDragging && id === activeDragId && draggedDimensionsRef.current) {
      const { width, height } = draggedDimensionsRef.current;
      style.width = `${width}px`;
      style.height = `${height}px`;
      style.minWidth = `${width}px`;
      style.maxWidth = `${width}px`;
      style.minHeight = `${height}px`;
      style.maxHeight = `${height}px`;
      style.flexShrink = 0;
      style.flexGrow = 0;
      style.flexBasis = 'auto';
      style.boxSizing = 'border-box';
    }

    const containerProps = {
      ref: wrappedSetNodeRef,
      style,
    };

    const dragHandleProps = {
      attributes: attributes || {},
      listeners,
      ref: handleRef,
      itemId: id,
    };

    return {
      dragHandleProps,
      containerProps,
      isDragging,
      isSettling,
    };
  };

  return {
    dndContextProps,
    sortableContextProps,
    containerWrapperProps,
    useSortableItem,
    FocusContext,
    focusContextValue,
    SettlingContext,
    settlingContextValue: isSettling,
    activeDragId,
    DragOverlay,
  };
};
